// apps/vendor/src/pages/settings/ProfilePage.tsx
//
// Phase 1 Task 49 — uvendor profile settings.
// PATCH /api/v1/users/me/profile — fields: firstName, lastName, phone,
// timezone, avatarUrl, language... / API-CONTRACTS L3441.
//
// CONTEXT.md: (survey gate enforced server-side; FE just renders).
// Audit log entry written by BE on PATCH (Acceptance: profile update → DB +
// audit row).

import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useAuth,
  useToast,
  SettingsLayout,
} from '@printway/ui';

import { NotificationChannelsSection } from './NotificationChannelsSection';

interface ProfileForm {
  firstName: string;
  lastName: string;
  phone: string;
  timezone: string;
  avatarUrl: string;
}

const INITIAL: ProfileForm = {
  firstName: '',
  lastName: '',
  phone: '',
  timezone: 'Asia/Ho_Chi_Minh',
  avatarUrl: '',
};

type LooseClient = {
  PATCH: (p: string, init?: { body?: unknown }) => Promise<{
    data?: unknown;
    error?: unknown;
    response: Response;
  }>;
};

export function ProfilePage(): JSX.Element {
  const { t } = useTranslation('settings');
  const user = useAuth((s) => s.user);
  const fetchMe = useAuth((s) => s.fetchMe);
  const { toast } = useToast();

  const [form, setForm] = useState<ProfileForm>(INITIAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null);
  // R4-3 (2026-05-16) — staged avatar: file kept in state + object URL
  // for preview, only POSTed when user bấm Lưu thay đổi.
  const [stagedAvatarFile, setStagedAvatarFile] = useState<File | null>(null);
  const [stagedAvatarPreview, setStagedAvatarPreview] = useState<string | null>(
    null,
  );

  // Release the object URL when component unmounts or file replaced.
  useEffect(() => {
    return () => {
      if (stagedAvatarPreview) URL.revokeObjectURL(stagedAvatarPreview);
    };
  }, [stagedAvatarPreview]);

  const handleAvatarSelect = (file: File): void => {
    setAvatarUploadError(null);
    if (file.size > 5 * 1024 * 1024) {
      setAvatarUploadError(t('profile.avatarTooLarge'));
      return;
    }
    if (stagedAvatarPreview) URL.revokeObjectURL(stagedAvatarPreview);
    setStagedAvatarFile(file);
    setStagedAvatarPreview(URL.createObjectURL(file));
  };

  const uploadStagedAvatar = async (): Promise<boolean> => {
    if (!stagedAvatarFile) return true;
    try {
      const fd = new FormData();
      fd.append('file', stagedAvatarFile);
      const base = (
        (import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } })
          .env?.VITE_API_BASE_URL ?? ''
      ).replace(/\/$/, '');
      const xsrf = (() => {
        const raw = document.cookie;
        for (const part of raw.split('; ')) {
          const eq = part.indexOf('=');
          if (eq < 0) continue;
          if (part.slice(0, eq) === 'XSRF-TOKEN') {
            try {
              return decodeURIComponent(part.slice(eq + 1));
            } catch {
              return part.slice(eq + 1);
            }
          }
        }
        return null;
      })();
      const res = await fetch(`${base}/api/v1/auth/me/avatar`, {
        method: 'POST',
        credentials: 'include',
        headers: xsrf ? { 'X-XSRF-TOKEN': xsrf } : undefined,
        body: fd,
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => null)) as
          | { error?: { code?: string; message?: string } }
          | null;
        setAvatarUploadError(
          j?.error?.message ?? j?.error?.code ?? `HTTP_${res.status}`,
        );
        return false;
      }
      if (stagedAvatarPreview) URL.revokeObjectURL(stagedAvatarPreview);
      setStagedAvatarFile(null);
      setStagedAvatarPreview(null);
      return true;
    } catch (e) {
      setAvatarUploadError(
        e instanceof Error ? e.message : 'AVATAR_UPLOAD_FAILED',
      );
      return false;
    }
  };

  // Hydrate form from current auth user on mount + when user changes.
  // UAT-1 fix (2026-05-16): use canonical firstName/lastName/phone/timezone
  // from BE instead of splitting displayName — splitting loses semantic
  // boundaries and resetting phone='' on every fetchMe wiped saved values.
  useEffect(() => {
    if (!user) return;
    const [fallbackFirst = '', ...rest] = (user.displayName ?? '').split(' ');
    setForm({
      firstName: user.firstName ?? fallbackFirst,
      lastName: user.lastName ?? rest.join(' '),
      phone: user.phone ?? '',
      timezone: user.timezone ?? 'Asia/Ho_Chi_Minh',
      avatarUrl: user.avatarUrl ?? '',
    });
  }, [user]);

  // Auto-hide success toast after 4s.
  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => setSuccess(false), 4000);
    return () => window.clearTimeout(t);
  }, [success]);

  const set = <K extends keyof ProfileForm>(k: K, v: ProfileForm[K]): void => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const handleSubmit = async (
    ev: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    ev.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      // R4-6 (2026-05-16): send firstName/lastName as separate fields so
      // BE preserves user-entered spacing (e.g. firstName="Nguyễn Văn"
      // stays intact instead of getting split into "Nguyễn" + "Văn").
      // Language is NOT sent here — the header LanguageSelector owns the
      // user's language preference (persists via cookie + PATCH on switch).
      // Keeping it in this form re-submitted a possibly-stale value on every
      // profile save, silently flipping the UI language.
      const body: Record<string, unknown> = {
        timezone: form.timezone,
      };
      const trimmedFirst = form.firstName.trim();
      const trimmedLast = form.lastName.trim();
      if (trimmedFirst.length > 0) body['firstName'] = trimmedFirst;
      if (trimmedLast.length > 0) body['lastName'] = trimmedLast;
      if (form.phone) body['phone'] = form.phone;

      const client = apiClient() as unknown as LooseClient;
      const { response, data } = await client.PATCH(
        '/api/v1/auth/me',
        { body },
      );
      if (!response.ok) {
        const env = data as { error?: { code?: string } } | undefined;
        const code = env?.error?.code ?? `HTTP_${response.status}`;
        setError(code);
        toast({
          variant: 'error',
          title: t('profile.toastSaveFailed'),
          description: code,
        });
        return;
      }
      // R4-3 (2026-05-16): upload staged avatar AFTER profile PATCH so the
      // single Lưu thay đổi button is the only commit point.
      if (stagedAvatarFile) {
        const ok = await uploadStagedAvatar();
        if (!ok) {
          toast({
            variant: 'error',
            title: t('profile.toastAvatarFailed'),
            description: avatarUploadError ?? t('profile.toastTryAgain'),
          });
          return;
        }
      }
      setSuccess(true);
      toast({
        variant: 'success',
        title: t('profile.toastSavedTitle'),
      });
      // Refresh useAuth so TopBar avatar/name reflect the change.
      await fetchMe();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'PROFILE_UPDATE_FAILED';
      setError(msg);
      toast({ variant: 'error', title: t('profile.toastUnknown'), description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsLayout portal="vendor" activeTab="profile">
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.cardTitle')}</CardTitle>
          <CardDescription>
            {t('profile.cardDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit} data-testid="profile-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField>
                <FormLabel htmlFor="firstName">{t('profile.firstName')}</FormLabel>
                <FormControl>
                  <Input
                    id="firstName"
                    value={form.firstName}
                    onChange={(e) => set('firstName', e.target.value)}
                    autoComplete="given-name"
                  />
                </FormControl>
              </FormField>
              <FormField>
                <FormLabel htmlFor="lastName">{t('profile.lastName')}</FormLabel>
                <FormControl>
                  <Input
                    id="lastName"
                    value={form.lastName}
                    onChange={(e) => set('lastName', e.target.value)}
                    autoComplete="family-name"
                  />
                </FormControl>
              </FormField>
            </div>

            <FormField>
              <FormLabel htmlFor="phone">{t('profile.phone')}</FormLabel>
              <FormControl>
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="+84xxxxxxxxx"
                  autoComplete="tel"
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="timezone">{t('profile.timezone')}</FormLabel>
              <FormControl>
                <Select
                  value={form.timezone}
                  onValueChange={(value) => set('timezone', value)}
                >
                  <SelectTrigger id="timezone">
                    <SelectValue placeholder={t('profile.timezone')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Asia/Ho_Chi_Minh">Asia/Ho_Chi_Minh (UTC+7)</SelectItem>
                    <SelectItem value="Asia/Bangkok">Asia/Bangkok (UTC+7)</SelectItem>
                    <SelectItem value="Asia/Singapore">Asia/Singapore (UTC+8)</SelectItem>
                    <SelectItem value="Asia/Tokyo">Asia/Tokyo (UTC+9)</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">America/New_York (UTC-5)</SelectItem>
                    <SelectItem value="America/Los_Angeles">America/Los_Angeles (UTC-8)</SelectItem>
                    <SelectItem value="Europe/London">Europe/London (UTC+0)</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="avatarFile">{t('profile.avatarLabel')}</FormLabel>
              <div className="flex items-center gap-3">
                {stagedAvatarPreview || form.avatarUrl ? (
                  <img
                    src={stagedAvatarPreview ?? form.avatarUrl}
                    alt={t('profile.avatarAlt')}
                    className="h-16 w-16 rounded-full object-cover ring-1 ring-pw-border"
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-pw-surface text-xs text-pw-text-muted ring-1 ring-pw-border"
                  >
                    n/a
                  </div>
                )}
                <input
                  id="avatarFile"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleAvatarSelect(file);
                  }}
                  data-testid="profile-avatar-file"
                  className="block text-sm text-pw-text file:mr-3 file:rounded-md file:border file:border-pw-border file:bg-pw-surface file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-pw-text hover:file:bg-pw-bg"
                />
              </div>
              <p className="mt-1 text-xs text-pw-text-muted">
                {t('profile.avatarHintPrefix')} <strong>{t('profile.saveChanges')}</strong>{t('profile.avatarHintSuffix')}
              </p>
              {stagedAvatarFile ? (
                <p className="mt-1 text-xs text-yellow-700">
                  {t('profile.avatarStaged')}
                </p>
              ) : null}
              {avatarUploadError ? (
                <p className="mt-1 text-xs text-red-700">
                  {avatarUploadError}
                </p>
              ) : null}
            </FormField>

            {error ? (
              <FormMessage data-testid="profile-error">{error}</FormMessage>
            ) : null}
            {success ? (
              <div
                role="status"
                aria-live="polite"
                data-testid="profile-success"
                className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800 dark:border-green-700/60 dark:bg-green-900/20 dark:text-green-200"
              >
                {t('profile.saveSuccess')}
              </div>
            ) : null}

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={saving} data-testid="profile-submit">
                {saving ? t('profile.saving') : t('profile.saveChanges')}
              </Button>
            </div>
          </Form>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardContent className="pt-6">
          <NotificationChannelsSection />
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
